import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { Post, EmojiType, Comment, Category, PostLanguage } from '@/types/post';

interface NewPostData {
  slides: string[];
  titleEn: string;
  titleKo: string;
  bodyEn: string;
  bodyKo: string;
  category: Category;
  summaryEn?: string;
  summaryKo?: string;
  language: PostLanguage;
}

interface UpdatePostData {
  slides?: string[];
  titleEn?: string;
  titleKo?: string;
  bodyEn?: string;
  bodyKo?: string;
  category?: Category;
  summaryEn?: string;
  summaryKo?: string;
  language?: PostLanguage;
}

interface PostsContextType {
  posts: Post[];
  isLoading: boolean;
  reactToPost: (postId: string, emoji: EmojiType, fingerprint: string) => void;
  addComment: (postId: string, author: string, text: string, userId?: string | null) => Promise<void>;
  deleteComment: (postId: string, commentId: string) => Promise<void>;
  blockComment: (postId: string, commentId: string, blocked?: boolean) => Promise<void>;
  editComment: (postId: string, commentId: string, newText: string) => Promise<void>;
  toggleComments: (postId: string) => Promise<void>;
  togglePostPublic: (postId: string) => Promise<void>;
  addPost: (data: NewPostData) => Promise<void>;
  updatePost: (postId: string, data: UpdatePostData) => Promise<void>;
  deletePost: (postId: string) => Promise<void>;
  refreshPosts: () => Promise<void>;
  trackPostView: (postId: string) => Promise<void>;
}

const dbPostToPost = (dbPost: any): Post => {
  const comments: Comment[] = (dbPost.comments || [])
    .filter((c: any) => c !== null)
    .map((c: any) => ({
      id: String(c.id),
      author: c.author,
      text: c.text,
      date: c.created_at ? c.created_at.split('T')[0] : '',
      blocked: c.blocked || false,
      userId: c.user_id || null,
    }));

  // Build slides from post_slides table (new way), fallback to posts.slides (legacy)
  let slides: string[] = [];
  if (dbPost.post_slides && Array.isArray(dbPost.post_slides)) {
    slides = dbPost.post_slides
      .filter((s: any) => s !== null)
      .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((s: any) => s.image_data);
  }
  if (slides.length === 0 && Array.isArray(dbPost.slides)) {
    slides = dbPost.slides;
  }

  return {
    id: String(dbPost.id),
    slug: dbPost.slug,
    title: { en: dbPost.title_en || '', ko: dbPost.title_ko || '' },
    coverImage: slides[0] || dbPost.cover_image || '',
    slides,
    summary: { en: dbPost.summary_en || '', ko: dbPost.summary_ko || '' },
    body: { en: dbPost.body_en || '', ko: dbPost.body_ko || '' },
    category: dbPost.category || 'Uncategorized',
    date: dbPost.post_date || dbPost.created_at?.split('T')[0] || '',
    featured: dbPost.featured || false,
    emojiReactions: dbPost.emoji_reactions || { '❤️': 0, '😮': 0, '😢': 0, '😄': 0, '🤔': 0, '🔥': 0, '✊': 0 },
    comments,
    commentsEnabled: dbPost.comments_enabled !== false,
    isPublic: dbPost.is_public !== false,
    readTime: dbPost.read_time || 1,
    viewCount: dbPost.view_count || 0,
    language: (dbPost.language as PostLanguage) || 'both',
  };
};

const PostsContext = createContext<PostsContextType>({
  posts: [],
  isLoading: true,
  reactToPost: () => {},
  addComment: async () => {},
  deleteComment: async () => {},
  blockComment: async () => {},
  editComment: async () => {},
  toggleComments: async () => {},
  togglePostPublic: async () => {},
  addPost: async () => {},
  updatePost: async () => {},
  deletePost: async () => {},
  refreshPosts: async () => {},
  trackPostView: async () => {},
});

export const PostsProvider = ({ children }: { children: ReactNode }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('posts')
      .select('*, comments(*), post_slides(*)')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    } else if (data) {
      setPosts(data.map(dbPostToPost));
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const trackPostView = async (postId: string) => {
    const numericPostId = parseInt(postId, 10);
    if (Number.isNaN(numericPostId)) return;

    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id || null;

    let isAdmin = false;
    if (userId) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', userId)
        .maybeSingle();
      isAdmin = profile?.is_admin || false;
    }

    await supabase.from('post_views').insert({
      post_id: numericPostId,
      user_id: userId,
      is_admin: isAdmin,
    });

    const { data: postData } = await supabase
      .from('posts')
      .select('view_count')
      .eq('id', numericPostId)
      .maybeSingle();

    const newCount = (postData?.view_count || 0) + 1;
    await supabase
      .from('posts')
      .update({ view_count: newCount })
      .eq('id', numericPostId);

    setPosts((prev) =>
      prev.map((p) => (p.id === postId ? { ...p, viewCount: newCount } : p))
    );
  };

  const reactToPost = async (postId: string, emoji: EmojiType, fingerprint: string) => {
    const post = posts.find((p) => p.id === postId);
    if (!post) return;

    const currentCount = post.emojiReactions[emoji] || 0;
    const newReactions = { ...post.emojiReactions, [emoji]: currentCount + 1 };

    setPosts((prev) =>
      prev.map((p) => (p.id === postId ? { ...p, emojiReactions: newReactions } : p))
    );

    try {
      const { data, error } = await supabase.functions.invoke('update-reaction', {
        body: { postId: parseInt(postId, 10), emoji, fingerprint },
      });

      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || 'Failed to save reaction');
      }

      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, emojiReactions: data.emoji_reactions } : p))
      );
    } catch (err: any) {
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, emojiReactions: post.emojiReactions } : p))
      );
      throw new Error(err.message || 'Failed to save reaction');
    }
  };

  const addComment = async (postId: string, author: string, text: string, userId?: string | null) => {
    const numericPostId = parseInt(postId, 10);
    if (Number.isNaN(numericPostId)) {
      throw new Error('Invalid post ID');
    }

    const insertData: any = { post_id: numericPostId, author: author.trim(), text: text.trim() };
    if (userId) insertData.user_id = userId;

    const { data, error } = await supabase
      .from('comments')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('Error adding comment:', error);
      throw new Error(`Failed to add comment: ${error.message}`);
    }

    if (data) {
      const newComment: Comment = {
        id: String(data.id),
        author: data.author,
        text: data.text,
        date: data.created_at ? data.created_at.split('T')[0] : '',
        blocked: false,
        userId: data.user_id || null,
      };
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, comments: [...p.comments, newComment] } : p
        )
      );
    }
  };

  const editComment = async (postId: string, commentId: string, newText: string) => {
    const numericCommentId = parseInt(commentId, 10);
    if (Number.isNaN(numericCommentId)) return;

    const { error } = await supabase
      .from('comments')
      .update({ text: newText.trim() })
      .eq('id', numericCommentId);

    if (error) {
      console.error('Error editing comment:', error);
      return;
    }

    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, comments: p.comments.map((c) => (c.id === commentId ? { ...c, text: newText.trim() } : c)) }
          : p
      )
    );
  };

  const deleteComment = async (postId: string, commentId: string) => {
    const numericCommentId = parseInt(commentId, 10);
    if (Number.isNaN(numericCommentId)) return;

    const { error } = await supabase.from('comments').delete().eq('id', numericCommentId);
    if (error) {
      console.error('Error deleting comment:', error);
      return;
    }

    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, comments: p.comments.filter((c) => c.id !== commentId) }
          : p
      )
    );
  };

  const blockComment = async (postId: string, commentId: string, blocked?: boolean) => {
    const numericCommentId = parseInt(commentId, 10);
    if (Number.isNaN(numericCommentId)) return;

    const newBlocked = blocked !== undefined ? blocked : true;

    const { error } = await supabase
      .from('comments')
      .update({ blocked: newBlocked })
      .eq('id', numericCommentId);

    if (error) {
      console.error('Error blocking comment:', error);
      return;
    }

    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, comments: p.comments.map((c) => (c.id === commentId ? { ...c, blocked: newBlocked } : c)) }
          : p
      )
    );
  };

  const toggleComments = async (postId: string) => {
    const post = posts.find((p) => p.id === postId);
    if (!post) return;

    const newValue = !post.commentsEnabled;
    const numericPostId = parseInt(postId, 10);

    setPosts((prev) =>
      prev.map((p) => (p.id === postId ? { ...p, commentsEnabled: newValue } : p))
    );

    await supabase
      .from('posts')
      .update({ comments_enabled: newValue })
      .eq('id', numericPostId);
  };

  const togglePostPublic = async (postId: string) => {
    const post = posts.find((p) => p.id === postId);
    if (!post) return;

    const newValue = !post.isPublic;
    const numericPostId = parseInt(postId, 10);

    setPosts((prev) =>
      prev.map((p) => (p.id === postId ? { ...p, isPublic: newValue } : p))
    );

    const { error } = await supabase
      .from('posts')
      .update({ is_public: newValue })
      .eq('id', numericPostId);

    if (error) {
      console.error('Error toggling post public:', error);
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, isPublic: !newValue } : p))
      );
    }
  };

  const addPost = async (data: NewPostData) => {
    // Cross-language fallback: if one title is empty, use the other
    const effectiveTitleEn = data.titleEn?.trim() || data.titleKo?.trim() || 'Untitled';
    const effectiveTitleKo = data.titleKo?.trim() || data.titleEn?.trim() || '제목 없음';

    // Generate slug from whichever title is available
    let slug: string;
    if (data.titleEn?.trim()) {
      slug = data.titleEn.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    } else {
      slug = 'post-' + Date.now();
    }
    slug = slug || 'post-' + Date.now();

    // Check for slug collision
    const { data: existing } = await supabase.from('posts').select('id').eq('slug', slug).maybeSingle();
    if (existing) {
      slug = `${slug}-${Date.now()}`;
    }

    // Insert post with lightweight URL array (images already uploaded to Storage)
    const lightPostData = {
      slug,
      title_en: effectiveTitleEn,
      title_ko: effectiveTitleKo,
      summary_en: data.summaryEn || '',
      summary_ko: data.summaryKo || '',
      body_en: data.bodyEn || '',
      body_ko: data.bodyKo || '',
      cover_image: data.slides[0] || '',
      slides: data.slides, // Storage public URLs — lightweight!
      category: data.category,
      featured: false,
      is_public: true,
      emoji_reactions: { '❤️': 0, '😮': 0, '😢': 0, '😄': 0, '🤔': 0, '🔥': 0, '✊': 0 },
      comments_enabled: true,
      read_time: 1,
      view_count: 0,
      language: data.language,
    };

    const { data: inserted, error: insertError } = await supabase
      .from('posts')
      .insert(lightPostData)
      .select('id')
      .single();

    if (insertError || !inserted) {
      console.error('Error adding post:', insertError);
      let message = '게시물 저장에 실패했습니다.';
      if (insertError?.message?.includes('violates not-null')) {
        message = '필수 입력값이 누락되었습니다. 제목을 입력해주세요.';
      } else if (insertError?.message?.includes('timeout') || insertError?.message?.includes('canceling statement')) {
        message = '요청 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.';
      } else if (insertError?.message) {
        message = `게시물 저장 실패: ${insertError.message}`;
      }
      throw new Error(message);
    }

    const newPostId = inserted.id;

    // Build post object client-side to avoid extra DB round-trip
    const now = new Date();
    const newPost = dbPostToPost({
      id: newPostId,
      ...lightPostData,
      created_at: now.toISOString(),
      comments: [],
    });

    setPosts((prev) => [newPost, ...prev]);
  };

  const updatePost = async (postId: string, data: UpdatePostData) => {
    const numericPostId = parseInt(postId, 10);
    if (Number.isNaN(numericPostId)) return;

    const updateData: any = {};
    if (data.titleEn !== undefined) updateData.title_en = data.titleEn;
    if (data.titleKo !== undefined) updateData.title_ko = data.titleKo;
    if (data.bodyEn !== undefined) updateData.body_en = data.bodyEn;
    if (data.bodyKo !== undefined) updateData.body_ko = data.bodyKo;
    if (data.category !== undefined) updateData.category = data.category;
    if (data.summaryEn !== undefined) updateData.summary_en = data.summaryEn;
    if (data.summaryKo !== undefined) updateData.summary_ko = data.summaryKo;
    if (data.language !== undefined) updateData.language = data.language;

    // If slides are updated, just save lightweight URLs directly
    if (data.slides !== undefined) {
      // Clear old post_slides so dbPostToPost correctly falls back to posts.slides
      await supabase.from('post_slides').delete().eq('post_id', numericPostId);

      updateData.cover_image = data.slides[0] || '';
      updateData.slides = data.slides;
    }

    const { error } = await supabase
      .from('posts')
      .update(updateData)
      .eq('id', numericPostId);

    if (error) {
      console.error('Error updating post:', error);
      throw new Error(`Failed to update post: ${error.message}`);
    }

    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? {
              ...p,
              ...(data.slides !== undefined && {
                slides: data.slides,
                coverImage: data.slides[0] || p.coverImage,
              }),
              ...(data.titleEn !== undefined && { title: { ...p.title, en: data.titleEn } }),
              ...(data.titleKo !== undefined && { title: { ...p.title, ko: data.titleKo } }),
              ...(data.bodyEn !== undefined && { body: { ...p.body, en: data.bodyEn } }),
              ...(data.bodyKo !== undefined && { body: { ...p.body, ko: data.bodyKo } }),
              ...(data.category !== undefined && { category: data.category }),
              ...(data.summaryEn !== undefined && { summary: { ...p.summary, en: data.summaryEn } }),
              ...(data.summaryKo !== undefined && { summary: { ...p.summary, ko: data.summaryKo } }),
              ...(data.language !== undefined && { language: data.language }),
            }
          : p
      )
    );
  };

  const deletePost = async (postId: string) => {
    const numericPostId = parseInt(postId, 10);
    if (Number.isNaN(numericPostId)) return;

    // post_slides will be cascade-deleted by FK constraint
    const { error } = await supabase.from('posts').delete().eq('id', numericPostId);
    if (error) {
      console.error('Error deleting post:', error);
      return;
    }

    setPosts((prev) => prev.filter((p) => p.id !== postId));
  };

  const refreshPosts = async () => {
    await fetchPosts();
  };

  return (
    <PostsContext.Provider
      value={{ posts, isLoading, reactToPost, addComment, deleteComment, blockComment, editComment, toggleComments, togglePostPublic, addPost, updatePost, deletePost, refreshPosts, trackPostView }}
    >
      {children}
    </PostsContext.Provider>
  );
};

export const usePosts = () => useContext(PostsContext);